import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SavedRecipesService, SavedRecipe } from '../lib/savedRecipesService'
import { FirestoreService, RecipeRating } from '../services/FirestoreService'
import { GeneratedRecipe } from '../lib/claudeRecipeGeneration'
import LoadingSpinner from './LoadingSpinner'
import Modal from './Modal'
import StarRating from './StarRating'
import RatingModal from './RatingModal'
import './SavedRecipes.css'

const SavedRecipes: React.FC = () => {
  const { user } = useAuth()
  const [savedRecipes, setSavedRecipes] = useState<SavedRecipe[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRecipe, setSelectedRecipe] = useState<GeneratedRecipe | null>(null)
  const [showFullRecipe, setShowFullRecipe] = useState(false)
  const [deletingRecipe, setDeletingRecipe] = useState<string | null>(null)

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [recipeToRate, setRecipeToRate] = useState<SavedRecipe | null>(null)
  const [userRatings, setUserRatings] = useState<Map<string, RecipeRating>>(new Map())

  useEffect(() => {
    const loadSavedRecipes = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        const recipes = await SavedRecipesService.getUserRecipes(user.id)
        setSavedRecipes(recipes)
      } catch (err) {
        console.error('Failed to load saved recipes:', err)
        setError('Failed to load your saved recipes. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadSavedRecipes()
  }, [user])

  // Load user ratings
  useEffect(() => {
    const loadUserRatings = async () => {
      if (user) {
        try {
          const ratings = await FirestoreService.getUserRatings(user.id)
          const ratingsMap = new Map(ratings.map(r => [r.recipe_id, r]))
          setUserRatings(ratingsMap)
        } catch (error) {
          console.error('Failed to load user ratings:', error)
        }
      }
    }
    loadUserRatings()
  }, [user])

  const viewFullRecipe = (recipe: SavedRecipe) => {
    const generatedRecipe = SavedRecipesService.savedRecipeToGenerated(recipe)
    setSelectedRecipe(generatedRecipe)
    setShowFullRecipe(true)
  }

  const closeFullRecipe = useCallback(() => {
    setShowFullRecipe(false)
    setSelectedRecipe(null)
  }, [])

  const deleteRecipe = async (recipeId: string) => {
    if (!user) return

    setDeletingRecipe(recipeId)
    try {
      const success = await SavedRecipesService.deleteRecipe(user.id, recipeId)
      if (success) {
        setSavedRecipes(prev => prev.filter(r => r.id !== recipeId))
      }
    } catch (err) {
      console.error('Failed to delete recipe:', err)
    } finally {
      setDeletingRecipe(null)
    }
  }

  const openRatingModal = (recipe: SavedRecipe) => {
    setRecipeToRate(recipe)
    setShowRatingModal(true)
  }

  const handleRatingSubmit = async (ratingData: Omit<RecipeRating, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return

    const ratingWithUser = { ...ratingData, user_id: user.id }
    const savedRating = await FirestoreService.rateRecipe(ratingWithUser)

    // Update local ratings cache
    setUserRatings(prev => new Map(prev).set(savedRating.recipe_id, savedRating))
  }

  // Handle escape key for modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showRatingModal) {
        setShowRatingModal(false)
        setRecipeToRate(null)
      }
      if (event.key === 'Escape' && showFullRecipe) {
        closeFullRecipe()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [showFullRecipe, showRatingModal, closeFullRecipe])

  if (loading) {
    return <LoadingSpinner message="Loading your saved recipes..." />
  }

  if (error) {
    return (
      <div className="saved-recipes error" role="alert">
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="saved-recipes">
      <div className="saved-recipes-header">
        <h2>Your Saved Recipes</h2>
        <p className="saved-recipes-subtitle">
          {savedRecipes.length === 0
            ? "You haven't saved any recipes yet. Go to Recipe Discovery to find and save recipes!"
            : `You have ${savedRecipes.length} saved recipe${savedRecipes.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>

      {savedRecipes.length > 0 && (
        <div className="saved-recipe-grid" role="list" aria-label="Your saved recipes">
          {savedRecipes.map(recipe => (
            <div key={recipe.id} className="saved-recipe-card" role="listitem">
              <div className="saved-recipe-header">
                <h4>{recipe.title}</h4>
                <div className="saved-recipe-meta" aria-label="Recipe metadata">
                  <span className="cuisine" aria-label={`Cuisine: ${Array.isArray(recipe.cuisine) ? recipe.cuisine.join(', ') : recipe.cuisine}`}>
                    {Array.isArray(recipe.cuisine) ? recipe.cuisine.join(', ') : recipe.cuisine}
                  </span>
                  <span className="time" aria-label={`Total time: ${recipe.prep_time + recipe.cook_time} minutes`}>{recipe.prep_time + recipe.cook_time} min</span>
                  <span className="difficulty" aria-label={`Difficulty: ${recipe.difficulty}`}>{recipe.difficulty}</span>
                </div>
              </div>

              <p className="saved-recipe-description">{recipe.description}</p>

              {recipe.dietary_tags && recipe.dietary_tags.length > 0 && (
                <div className="dietary-tags" role="list" aria-label="Dietary tags">
                  {recipe.dietary_tags.map((tag, index) => (
                    <span key={index} className="dietary-tag" role="listitem">{tag}</span>
                  ))}
                </div>
              )}

              {userRatings.has(recipe.id) && (
                <div className="recipe-rating-display">
                  <StarRating
                    rating={userRatings.get(recipe.id)!.rating}
                    readonly
                    size="small"
                  />
                  {userRatings.get(recipe.id)!.would_make_again && (
                    <span className="would-make-again">Would make again</span>
                  )}
                </div>
              )}

              <div className="saved-recipe-actions">
                <button
                  className="view-recipe-btn"
                  onClick={() => viewFullRecipe(recipe)}
                  aria-label={`View full recipe for ${recipe.title}`}
                >
                  View Full Recipe
                </button>
                <button
                  className="rate-recipe-btn"
                  onClick={() => openRatingModal(recipe)}
                  aria-label={`Rate ${recipe.title}`}
                >
                  {userRatings.has(recipe.id) ? '⭐ Update Rating' : '⭐ Rate'}
                </button>
                <button
                  className="delete-recipe-btn"
                  onClick={() => deleteRecipe(recipe.id)}
                  disabled={deletingRecipe === recipe.id}
                  aria-label={`Delete ${recipe.title}`}
                >
                  {deletingRecipe === recipe.id ? 'Deleting...' : '🗑️ Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full Recipe Modal */}
      {showFullRecipe && selectedRecipe && (
        <Modal
          isOpen={showFullRecipe}
          onClose={closeFullRecipe}
          title={selectedRecipe.title}
          closeButtonLabel="Close recipe details"
          className="recipe-modal"
        >
          <div className="recipe-details">
            <div className="recipe-meta" role="list" aria-label="Recipe details">
              <span role="listitem" aria-label={`Preparation time: ${selectedRecipe.prep_time} minutes`}>🕐 Prep: {selectedRecipe.prep_time}min</span>
              <span role="listitem" aria-label={`Cooking time: ${selectedRecipe.cook_time} minutes`}>🔥 Cook: {selectedRecipe.cook_time}min</span>
              <span role="listitem" aria-label={`Serves ${selectedRecipe.servings} people`}>👥 Serves: {selectedRecipe.servings}</span>
              <span role="listitem" aria-label={`Difficulty level: ${selectedRecipe.difficulty}`}>📊 {selectedRecipe.difficulty}</span>
            </div>

            <div className="recipe-cuisine-tags">
              <span className="cuisine-tag">
                {Array.isArray(selectedRecipe.cuisine)
                  ? selectedRecipe.cuisine.join(', ')
                  : selectedRecipe.cuisine}
              </span>
              {selectedRecipe.dietary_tags && selectedRecipe.dietary_tags.length > 0 && (
                selectedRecipe.dietary_tags.map((tag, index) => (
                  <span key={index} className="dietary-tag">{tag}</span>
                ))
              )}
            </div>

            <p className="recipe-description">{selectedRecipe.description}</p>
          </div>

          <div className="recipe-sections">
            <section className="ingredients-section" aria-labelledby="ingredients-heading">
              <h3 id="ingredients-heading">🥘 Ingredients</h3>
              <ul className="ingredients-list">
                {selectedRecipe.ingredients.map((ingredient, index) => (
                  <li key={index}>{ingredient}</li>
                ))}
              </ul>
            </section>

            <section className="instructions-section" aria-labelledby="instructions-heading">
              <h3 id="instructions-heading">📋 Instructions</h3>
              <ol className="instructions-list">
                {selectedRecipe.instructions.map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))}
              </ol>
            </section>

            {selectedRecipe.tips && selectedRecipe.tips.length > 0 && (
              <section className="tips-section" aria-labelledby="tips-heading">
                <h3 id="tips-heading">💡 Tips</h3>
                <ul className="tips-list">
                  {selectedRecipe.tips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <div className="recipe-modal-actions">
            <button
              className="print-recipe-btn"
              onClick={() => window.print()}
              aria-label="Print this recipe"
            >
              🖨️ Print Recipe
            </button>
          </div>
        </Modal>
      )}

      {/* Rating Modal */}
      {recipeToRate && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false)
            setRecipeToRate(null)
          }}
          recipeId={recipeToRate.id}
          recipeTitle={recipeToRate.title}
          existingRating={userRatings.get(recipeToRate.id) || null}
          onSubmit={handleRatingSubmit}
        />
      )}
    </div>
  )
}

export default SavedRecipes
